// Die Höfe im Client: Seit task-013 entscheidet der Server über Tiere, Tor und Baum, und der
// Client zeigt nur, was in `farm`-Nachrichten ankommt (docs/architektur-paket-2.md, Abschnitt
// „Client (game/)“, ADR 0027). Reine Logik, kein DOM und kein Three.js.
//
// Zwei Umrechnungen passieren hier, und nur hier:
//
//  1. **Die Uhr.** `AnimalState.productReadyTick` zählt in Ticks der Hof-Uhr, und jeder Hof hat
//     seine eigene, die nur läuft, solange ihr Besitzer da ist. Der Client hat davon nur seinen
//     eigenen Takt. Beim Annehmen einer Nachricht wird die Bereitschaft deshalb auf die Uhr des
//     Clients umgerechnet: „noch so viele Ticks“ bleibt „noch so viele Ticks“. Danach rechnen
//     `isProductReady` und `availableActions` wie gewohnt mit `state.tick`.
//  2. **Die Bewegung.** Höfe kommen fünfmal je Sekunde, gezeichnet wird sechzigmal. Zwischen den
//     letzten zwei Nachrichten wird interpoliert, mit `TIER_VERZUG_MS` Verzug – so ist die
//     Strecke, die gezeigt wird, immer schon bekannt und muss nie geraten werden.
import { TICK_RATE, lerp, type AnimalState, type FarmState } from '@bauernhof/shared';

/**
 * So weit hinkt die Anzeige der Tiere hinter dem Server her. Ein Hof kommt alle 200 ms; mit
 * diesem Verzug liegt der gezeigte Zeitpunkt stets zwischen zwei empfangenen Nachrichten.
 */
export const TIER_VERZUG_MS = 200;

/** Eine Änderung am Tor eines Hofs; der Client setzt daraufhin den Block und meshed neu. */
export interface TorAenderung {
  parcelId: number;
  open: boolean;
}

/** Eine Änderung der Äpfel am Baum eines Hofs; der Client setzt daraufhin die Krone (task-014). */
export interface BaumAenderung {
  parcelId: number;
  apples: number;
}

export interface HofAnsicht {
  /**
   * Nimmt einen Hofzustand an. `empfangen` ist die Zeit des Eingangs (`performance.now()`),
   * `clientTick` der aktuelle Takt des Clients, auf den die Bereitschaft umgerechnet wird.
   */
  wendeAn(farm: FarmState, empfangen: number, clientTick: number): void;
  /** Alle bekannten Höfe, in der Reihenfolge ihres ersten Auftauchens. */
  hoefe(): FarmState[];
  /** Der Hof einer Parzelle, oder undefined. */
  hof(parcelId: number): FarmState | undefined;
  /**
   * Die Tiere aller Höfe zum Zeitpunkt `jetzt`, zwischen den letzten zwei Nachrichten.
   * `clientTick` ist der aktuelle Takt des Clients: Auf einem Hof ohne anwesenden Besitzer steht
   * die Hof-Uhr, und die Bereitschaft wandert mit dem Takt mit, statt mit ihm abzulaufen.
   */
  tiere(jetzt: number, clientTick: number): AnimalState[];
  /** Die Tor-Änderungen seit dem letzten Aufruf; jede kommt genau einmal. */
  nimmTorAenderungen(): TorAenderung[];
  /**
   * Die Äpfel-Änderungen seit dem letzten Aufruf; jede kommt genau einmal. Die Welt wird mit allen
   * Äpfeln gebaut, darum meldet jeder Hof seine Äpfel beim ersten Mal, danach nur bei neuer Zahl.
   */
  nimmBaumAenderungen(): BaumAenderung[];
}

/** Ein Tier in zwei Ständen: dem vorletzten und dem letzten, mit ihren Empfangszeiten. */
interface TierSpur {
  vorher: AnimalState;
  vorherZeit: number;
  jetzt: AnimalState;
  jetztZeit: number;
  /** Nummer der letzten Nachricht, in der das Tier vorkam; wer fehlt, ist verschwunden. */
  gesehen: number;
}

export function createHofAnsicht(): HofAnsicht {
  const hoefe = new Map<number, FarmState>();
  /** Der Takt des Clients beim Empfang der letzten Nachricht je Hof. */
  const empfangsTick = new Map<number, number>();
  const spuren = new Map<string, TierSpur>();
  const torAenderungen: TorAenderung[] = [];
  const baumAenderungen: BaumAenderung[] = [];
  let nachricht = 0;

  return {
    wendeAn(farm, empfangen, clientTick) {
      nachricht += 1;
      empfangsTick.set(farm.parcelId, clientTick);
      const vorher = hoefe.get(farm.parcelId);
      // Die Welt wird mit geschlossenen Toren gebaut; ein neuer Hof meldet darum nur ein offenes.
      if (vorher === undefined ? farm.gateOpen : vorher.gateOpen !== farm.gateOpen) {
        torAenderungen.push({ parcelId: farm.parcelId, open: farm.gateOpen });
      }
      if (vorher === undefined || vorher.tree.apples !== farm.tree.apples) {
        baumAenderungen.push({ parcelId: farm.parcelId, apples: farm.tree.apples });
      }

      const umgerechnet: FarmState = {
        ...farm,
        animals: farm.animals.map((tier) => aufClientUhr(tier, farm.farmSeconds, clientTick)),
      };
      hoefe.set(farm.parcelId, umgerechnet);

      for (const tier of umgerechnet.animals) {
        const spur = spuren.get(tier.id);
        if (spur === undefined) {
          spuren.set(tier.id, { vorher: tier, vorherZeit: empfangen, jetzt: tier, jetztZeit: empfangen, gesehen: nachricht });
          continue;
        }
        spur.vorher = spur.jetzt;
        spur.vorherZeit = spur.jetztZeit;
        spur.jetzt = tier;
        spur.jetztZeit = empfangen;
        spur.gesehen = nachricht;
      }

      // Tiere, die dieser Hof nicht mehr nennt, gibt es nicht mehr.
      const bekannt = new Set(umgerechnet.animals.map((tier) => tier.id));
      for (const [id, spur] of spuren) {
        if (spur.jetzt.penId === `parzelle-${farm.parcelId}` && !bekannt.has(id)) spuren.delete(id);
      }
    },

    hoefe: () => [...hoefe.values()],

    hof: (parcelId) => hoefe.get(parcelId),

    tiere(jetzt, clientTick) {
      const anzeige = jetzt - TIER_VERZUG_MS;
      const liste: AnimalState[] = [];
      for (const hofZustand of hoefe.values()) {
        // Steht die Hof-Uhr, bleibt der Rest bis „voll“ derselbe wie beim Empfang.
        const stillstand = hofZustand.ownerPresent ? 0 : clientTick - (empfangsTick.get(hofZustand.parcelId) ?? clientTick);
        for (const tier of hofZustand.animals) {
          const spur = spuren.get(tier.id);
          const stand = spur === undefined ? kopie(tier) : zwischenstand(spur, anzeige);
          stand.productReadyTick += stillstand;
          liste.push(stand);
        }
      }
      return liste;
    },

    nimmBaumAenderungen() {
      const alle = [...baumAenderungen];
      baumAenderungen.length = 0;
      return alle;
    },

    nimmTorAenderungen() {
      const alle = [...torAenderungen];
      torAenderungen.length = 0;
      return alle;
    },
  };
}

/**
 * Rechnet die Bereitschaft eines Tiers von der Hof-Uhr auf die Uhr des Clients um: Aus „ab
 * Hof-Tick x“ wird „ab Client-Tick jetzt + (x − Stand der Hof-Uhr)“. Der Stand kommt in ganzen
 * Hof-Sekunden, die Umrechnung ist damit auf eine Sekunde genau – für ein volles Euter reicht das.
 */
function aufClientUhr(tier: AnimalState, farmSeconds: number, clientTick: number): AnimalState {
  const rest = tier.productReadyTick - farmSeconds * TICK_RATE;
  return { ...kopie(tier), productReadyTick: clientTick + rest };
}

/** Eine eigenständige Kopie; die Ansicht darf nichts verändern, was noch einmal gebraucht wird. */
function kopie(tier: AnimalState): AnimalState {
  return { ...tier, pos: { ...tier.pos }, target: tier.target === null ? null : { ...tier.target } };
}

/** Der Stand zwischen den letzten zwei Nachrichten; außerhalb bleibt es beim jeweiligen Rand. */
function zwischenstand(spur: TierSpur, anzeige: number): AnimalState {
  const spanne = spur.jetztZeit - spur.vorherZeit;
  const alpha = spanne <= 0 ? 1 : Math.min(1, Math.max(0, (anzeige - spur.vorherZeit) / spanne));
  const ergebnis = kopie(spur.jetzt);
  ergebnis.pos = {
    x: lerp(spur.vorher.pos.x, spur.jetzt.pos.x, alpha),
    y: lerp(spur.vorher.pos.y, spur.jetzt.pos.y, alpha),
    z: lerp(spur.vorher.pos.z, spur.jetzt.pos.z, alpha),
  };
  ergebnis.yaw = kurzesterWinkel(spur.vorher.yaw, spur.jetzt.yaw, alpha);
  return ergebnis;
}

/** Interpoliert zwei Winkel über den kürzesten Weg; das Ergebnis liegt auf der Seite von `a`. */
function kurzesterWinkel(a: number, b: number, t: number): number {
  const zweiPi = 2 * Math.PI;
  const diff = ((((b - a + Math.PI) % zweiPi) + zweiPi) % zweiPi) - Math.PI;
  return a + diff * t;
}
